import './styles/DashboardCard.scss';

import { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Close, DragIndicator, AddTask } from '@mui/icons-material';
import { CircularProgress, Grid, Paper } from '@mui/material';
import { green } from '@mui/material/colors';
import { CreateTile, Tile } from '@thro-types/users';

import { CSSProperties, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useJobOfferingsQuery } from 'api/hooks/jobmarket/offers';
import i18next, { translations } from 'locales/i18n';

import { isInSelectedTilesArray } from '../helper';
import { selectTileDrawer } from '../selectors';
import { TileDrawerActions } from '../tileDrawerSlice';
import { DrawerMode } from '../types';

interface BaseProps {
  id: UniqueIdentifier;
  onClick?: Function;
  editMode?: boolean;
  fullWidth?: boolean;
  handleRemoveClick?: (tile: Tile) => void;
  tile: Tile | CreateTile;
  isSettingsView?: boolean;
  selectable?: boolean;
  selectedTiles?: Tile[];
}

interface PreviewProps extends BaseProps {
  id: 'preview';
  tile: CreateTile;
}
interface SelectableProps extends BaseProps {
  selectable: true;
  tile: Tile;
  selectedTiles: Tile[];
}

type PropUnion = BaseProps | PreviewProps | SelectableProps;

export const DashboardCard: FunctionComponent<PropUnion> = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedTilesToAdd } = useSelector(selectTileDrawer);

  const { data: offerData } = useJobOfferingsQuery(
    { params: { active: true } },
    props.tile.styleType === 'JOBMARKET'
  );

  const isSelected =
    props.selectable === true &&
    !!props.selectedTiles!.find(tile => {
      return 'id' in props.tile && tile.id === props.tile.id;
    });

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.id
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 0
  };

  const Card = cardProps => (
    <Grid
      item
      className={`dashboard-card`}
      xs={12}
      sm={!props.fullWidth && 6}
      lg={!props.fullWidth && 4 /*props.user?.role === 'Studenten' ? 3 : 4*/}
      onClick={props.onClick && props.onClick()}
      ref={setNodeRef}
      style={style}
      {...attributes}
      marginX={props.selectable ? 1 : 0}
    >
      {cardProps.children}
    </Grid>
  );

  const Body = () => (
    <Grid
      container
      className={`dashboard-card__body`}
      component={Paper}
      elevation={isDragging ? 20 : isSelected ? 6 : 2}
      sx={{
        border:
          props.selectable && isInSelectedTilesArray(props.tile as Tile, selectedTilesToAdd)
            ? 'solid 1px'
            : '',
        borderColor: green[600]
      }}
    >
      <Grid item xs={8}>
        <Grid className={'dashboard-card__body__addTaskIcon'}>
          {props.selectable && isInSelectedTilesArray(props.tile as Tile, selectedTilesToAdd) && (
            <AddTask sx={{ color: green[500], fontSize: 35 }}></AddTask>
          )}
        </Grid>
        <Grid container className="dashboard-card__body__title">
          {i18next.language === 'en' && props.tile.title_en?.length
            ? props.tile.title_en
            : props.tile.title_de || t(translations.pages.home.cardDrawer.placeholder.title())}
        </Grid>
        {props.tile.styleType === 'JOBMARKET' ? (
          <Grid container className="homepage-container__jobmarket">
            <div className="homepage-container__jobmarket__count">
              {offerData?.pagination.dataCount ?? <CircularProgress size={20} />}
            </div>
            {t(translations.pages.jobOffers.jobOffers())}
          </Grid>
        ) : (
          <Grid container className="homepage-c ontainer__card">
            {i18next.language === 'en' && props.tile.shortText_en?.length
              ? props.tile.shortText_en
              : props.tile.shortText_de ||
                t(translations.pages.home.cardDrawer.placeholder.description())}
          </Grid>
        )}
      </Grid>
      <Grid container item xs={4} className="dashboard-card__body__image">
        {props.editMode && (
          <Grid
            container
            style={{ justifyContent: 'flex-end', borderWidth: 1, borderColor: 'black' }}
          >
            <button
              className="dashboard-card__body__remove"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                props.handleRemoveClick &&
                  props.id !== 'preview' &&
                  props.handleRemoveClick(props.tile as Tile);
              }}
            >
              <Close />
            </button>
            <button className="dashboard-card__body__drag" {...listeners}>
              <DragIndicator />
            </button>
          </Grid>
        )}
        {props.tile.icon.path && (
          <img
            src={
              props.tile.icon.path.startsWith('blob:')
                ? props.tile.icon.path
                : `${import.meta.env.VITE_S3_IMAGE_PROXY}/${props.tile.icon.path}`
            }
            alt=""
            style={{
              maxWidth: 110,
              borderRadius: props.tile.icon.isRounded ? '50%' : undefined
            }}
          />
        )}
      </Grid>
    </Grid>
  );

  const renderBody = () => {
    // Helper function to handle settings view clicks
    const handleSettingsClick = () => {
      dispatch(TileDrawerActions.setTile(props.tile as Tile));
      dispatch(TileDrawerActions.setCurrentMode(DrawerMode.CREATE));
      dispatch(TileDrawerActions.open());
    };

    // Helper function to handle selectable tile clicks
    const handleSelectableClick = () => {
      const tile = props.tile as Tile;
      if (isInSelectedTilesArray(tile, selectedTilesToAdd))
        dispatch(TileDrawerActions.removeSelectedTileToAdd(tile.id));
      else {
        dispatch(TileDrawerActions.addSelectedTileToAdd(tile));
      }
    };

    // Render for selectable tiles
    if (props.selectable) {
      return (
        <div onClick={handleSelectableClick}>
          <Body />
        </div>
      );
    }

    // Render for settings view tile
    if (props.isSettingsView) {
      return (
        <div onClick={handleSettingsClick}>
          <Body />
        </div>
      );
    }
    if (props.id === 'preview' || props.editMode) {
      return <Body />;
    }
    // Render for external tiles
    if (props.tile?.isExternal) {
      return (
        <a href={props.tile.link} target="_blank" rel="noopener noreferrer">
          <Body />
        </a>
      );
    }

    // Render for internally linked tiles
    if (props.tile?.link) {
      return (
        <Link to={props.tile?.link}>
          <Body />
        </Link>
      );
    }

    // Default Render
    return <Body />;
  };

  return <Card>{renderBody()}</Card>;
};
